import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ProgressPanelService } from './progress-panel.service';
import { SecurityService } from 'src/@dva/services/security.service';
import { Workflow } from './models/workflow.model';
import { WorkflowItem } from './models/workflow-item.model';
import { WorkflowStatusEnum } from './models/workflow-status.enum';
import { WorkflowStatusItemEnum } from './models/workflow-status-item.enum';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/@dva/shared/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'dva-progress-panel',
  templateUrl: './progress-panel.component.html',
  styleUrls: ['./progress-panel.component.scss'],
  providers: [DatePipe],
})
export class ProgressPanelComponent implements OnInit {
  companyId: number;
  selectedWorkflow: Workflow;
  selectedItems: WorkflowItem[] = [];
  validationsAwaiting: Workflow[] = [];
  processingValidations: Workflow[] = [];
  validationsCompleted: Workflow[] = [];
  validationErrors: Workflow[] = [];
  date: Date;

  constructor(
    private securityService: SecurityService,
    private progressPanelService: ProgressPanelService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.companyId = this.securityService.getCompanyId();
    this.date = new Date();
    this.loadData();
  }

  refresh() {
    this.validationsAwaiting = [];
    this.processingValidations = [];
    this.validationsCompleted = [];
    this.selectedWorkflow = null;
    this.selectedItems = [];
    this.loadData();
  }

  hasValidationsAwaiting() {
    return this.validationsAwaiting.length > 0;
  }

  hasProcessingValidations() {
    return this.processingValidations.length > 0;
  }

  hasValidationsCompleted() {
    return this.validationsCompleted.length > 0;
  }

  hasValidationErrors() {
    return this.validationErrors.length > 0;
  }

  loadData() {
    if (!this.companyId) return;
    this.loadProgress();
  }

  private loadProgress() {
    this.progressPanelService.getByDateAndCompanyId(this.getDate(), this.companyId).subscribe((workflows) => {
      if (workflows.length > 0) {
        this.validationsAwaiting = workflows.filter(
          (workflow) => workflow.statusId == WorkflowStatusEnum.Waiting && workflow.errorId == 0,
        );
        this.processingValidations = workflows.filter(
          (workflow) => workflow.statusId == WorkflowStatusEnum.Processing && workflow.errorId == 0,
        );
        this.validationsCompleted = workflows.filter((workflow) => workflow.statusId == WorkflowStatusEnum.Finished);
        this.validationErrors = workflows.filter(
          (workflow) =>
            (workflow.statusId == WorkflowStatusEnum.Waiting || workflow.statusId == WorkflowStatusEnum.Processing) &&
            workflow.errorId > 0,
        );
      }
    });
  }

  hasSelectedWorkflow() {
    return this.selectedWorkflow && this.selectedItems.length > 0;
  }

  isProgressBar(item: WorkflowItem) {
    return this.isProcessingFile(item) || this.isValidatingDocuments(item);
  }

  isProcessingFile(item: WorkflowItem) {
    return item.statusItemId == WorkflowStatusItemEnum.ProcessingFile;
  }

  isValidatingDocuments(item: WorkflowItem): boolean {
    return item.statusItemId == WorkflowStatusItemEnum.ValidatingDocuments;
  }

  selectWorkflow(workflow: Workflow) {
    this.selectedWorkflow = workflow;
    this.refreshItems();
  }

  refreshItems() {
    this.selectedItems = [];
    this.progressPanelService.getItemsById(this.selectedWorkflow.id).subscribe((items) => {
      this.selectedItems = items;
    });
  }

  isWorkflowSelected(workflow: Workflow) {
    return this.selectedWorkflow && this.selectedWorkflow.id === workflow.id;
  }

  isFinishedWorkflow() {
    return this.selectedWorkflow && this.selectedWorkflow.statusId == WorkflowStatusEnum.Finished;
  }

  isWorkflowWithError() {
    return this.selectedWorkflow && this.selectedWorkflow.errorId > 0;
  }

  reprocess() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: 'Confirmation.ConfirmationReprocess',
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.progressPanelService.reprocessByWorkflowId(this.selectedWorkflow.id).subscribe(() => this.refresh());
        }
      });
  }

  cancel() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: 'Confirmation.ConfirmationCancel',
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.progressPanelService.cancelByWorkflowId(this.selectedWorkflow.id).subscribe(() => this.refresh());
        }
      });
  }

  private getDate() {
    return this.datePipe.transform(this.date, 'yyyy-MM-dd');
  }
}
